import {
  Box,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import ConditionalWrapper from "@splitfire-agency/raiden-library/dist/components/ConditionalWrapper";
import {
  forwardRef,
  Fragment,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { IoChevronDownOutline } from "react-icons/io5";
import { FormattedMessage, useIntl } from "react-intl";
import { SearchProvider } from "../../../contexts/Search";
import CommonOption from "./CommonOption";
import CommonSelectedOption from "./CommonSelectedOption";
import Internal from "./Internal";

/**
 * @typedef {(params: { item: object }) => void} ToggleValue
 * @typedef {(params: { value: any }) => void} RemoveValue
 *
 * @typedef {(params: { item: object }) => string} ResolveItemName
 * @typedef {(params: { item: object }) => any} ResolveItemValue
 *
 * @typedef {{ ref: any, value: any, item: object, resolveItemValue: ResolveItemValue, resolveItemName: ResolveItemName, toggleValue: ToggleValue, focusPrev: () => void, focusNext: () => void }} RenderOptionParams
 * @typedef {{ item: object, itemValue: (string | number), resolveItemName: ResolveItemName, removeValue: RemoveValue, hydrationDone: boolean, isInPopover: boolean }} RenderSelectedOptionParams
 *
 * @typedef {(params: RenderOptionParams) => import("react").ReactElement} RenderOption
 * @typedef {(params: RenderSelectedOptionParams) => import("react").ReactElement} RenderSelectedOption
 */

/** @type {RenderOption} */
function commonRenderOption({ ...props }) {
  return <CommonOption {...props} />;
}

/** @type {RenderSelectedOption} */
function commonRenderSelectedOption({ ...props }) {
  return <CommonSelectedOption {...props} />;
}

const Autocomplete = forwardRef(
  /**
   * @typedef {Object} Props
   * @property {(params: {fields: any}) => string} endpointUrl
   * @property {object} [defaultValues]
   * @property {string} name
   * @property {string | string[] | number | number[]} value the value of the form control, can be an array or a single value
   * @property {(params: {target: {name: string, value: string | number | (string | number)[]}}) => void} onChange
   * @property {boolean} [isRequired]
   * @property {boolean} [isDisabled]
   * @property {boolean} [isInvalid]
   * @property {boolean} [isMultiple]
   * @property {ResolveItemName} resolveItemName
   * @property {ResolveItemValue} resolveItemValue
   * @property {RenderOption} [renderOption]
   * @property {RenderSelectedOption} [renderSelectedOption]
   * @property {object} [valueItemMap] value-item map used for hydration
   * @property {boolean} [hydrationDone] once hydrated the component will display the values of items if it can't resolve their names
   *
   * @param {Props & import("@chakra-ui/react").BoxProps} props
   */
  function Autocomplete(props, ref) {
    const {
      endpointUrl,
      defaultValues: _defaultValues = {},
      name,
      value: _value,
      onChange: _onChange,
      isMultiple = false,
      resolveItemName,
      resolveItemValue,
      renderOption = commonRenderOption,
      renderSelectedOption = commonRenderSelectedOption,
      valueItemMap: hydrationValueItemMap,
      hydrationDone = true,
      ...otherProps
    } = props;

    delete otherProps["isRequired"];
    delete otherProps["isDisabled"];
    delete otherProps["isInvalid"];

    const defaultValues = { per_page: 5, term: "", ..._defaultValues };

    const intl = useIntl();

    const [valueItemMap, setValueItemMap] = useState({});

    const inputRef = useRef(null);

    const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");
    const borderColorInvalid = useColorModeValue("red.500", "red.300");
    const boxShadowInvalid = useColorModeValue(
      "0 0 0 1px var(--chakra-colors-red-500)",
      "0 0 0 1px var(--chakra-colors-red-300)",
    );

    /** @type {(string | number)[]} */
    const values = useMemo(() => {
      let value = [];
      if (Array.isArray(_value)) {
        value = _value;
      } else {
        if (_value !== "") {
          value = [_value];
        }
      }
      return value;
    }, [_value]);

    const onChange = useCallback(
      /** @type {(event: { target: { name: string, value: (string | number)[] }}, onClose?: () => void) => void} */
      (event, onClose = () => {}) => {
        if (isMultiple) {
          _onChange(event);
        } else {
          _onChange({
            target: {
              name,
              value:
                event?.target?.value?.[event?.target?.value?.length - 1] ?? "",
            },
          });
        }
        if (!isMultiple) {
          onClose();
        }
      },
      [_onChange, isMultiple, name],
    );

    // fixes the component if it is initialized with a value instead of an array
    if (isMultiple && !Array.isArray(_value)) {
      onChange({
        target: {
          name,
          value: [...(_value !== "" ? [_value] : [])],
        },
      });
    }

    // fixes the component if it is initialized with more than one value in 'single' mode
    if (!isMultiple && values.length > 1) {
      onChange({
        target: {
          name,
          value: [values[0] ?? ""],
        },
      });
    }

    const removeValue = useCallback(
      /**
       * @param {object} param0
       * @param {any} param0.value
       * @param {() => void} param0.onClose
       */
      ({ value: itemValue, onClose }) => {
        onChange(
          {
            target: {
              name,
              value: values.filter((item) => item !== itemValue),
            },
          },
          onClose,
        );
      },
      [name, onChange, values],
    );

    /**
     * @param {object} param0
     * @param {object} param0.item
     * @param {() => void} param0.onClose
     */
    function toggleValue({ item, onClose }) {
      const itemValue = resolveItemValue({ item });
      if (values.includes(itemValue)) {
        removeValue({ value: itemValue, onClose });
      } else {
        onChange({ target: { name, value: [...values, itemValue] } }, onClose);
        setValueItemMap((prev) => {
          return {
            ...prev,
            [itemValue]: item,
          };
        });
      }
    }

    const reset = useCallback(() => {
      onChange({ target: { name, value: [] } });
    }, [name, onChange]);

    /** return the item corresponding to a given value */
    const resolveItemByValue = useCallback(
      (itemValue) => {
        return (
          valueItemMap?.[itemValue] ??
          hydrationValueItemMap?.[itemValue] ??
          null
        );
      },
      [hydrationValueItemMap, valueItemMap],
    );

    return (
      <>
        {/* code nécessaire pour le fonctionnement de des tags du moteur de recherche */}

        {values?.map?.((value) => (
          <input
            ref={function (ref) {
              if (ref) {
                const valueIndex = values.indexOf(value);
                // @ts-ignore
                ref._newValue = [...values].filter(function (
                  _,
                  tempValueIndex,
                ) {
                  return tempValueIndex !== valueIndex;
                });
              }
            }}
            type="hidden"
            name={name}
            value={value}
            data-value={
              resolveItemName({ item: resolveItemByValue(value) }) ?? value
            }
            key={value}
          />
        ))}

        <Box {...otherProps}>
          <Popover
            isLazy={true}
            lazyBehavior="unmount"
            initialFocusRef={inputRef}
            placement="bottom"
            matchWidth={true}
            closeOnBlur={true}
            strategy="fixed"
            modifiers={[
              {
                name: "offset",
                options: {
                  offset: [0, -40],
                },
              },
            ]}>
            {({ onClose }) => (
              <>
                <PopoverTrigger>
                  <HStack
                    ref={ref}
                    tabIndex={props.isDisabled ? -1 : 0}
                    borderWidth="1px"
                    height="40px"
                    borderRadius="6px"
                    minWidth="200px"
                    position="relative"
                    borderColor={
                      props.isInvalid ? borderColorInvalid : borderColor
                    }
                    boxShadow={props.isInvalid ? boxShadowInvalid : undefined}
                    pl="1rem"
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        // @ts-ignore
                        event.target.click();
                      }
                    }}
                    opacity={props.isDisabled ? 0.5 : 1}
                    pointerEvents={props.isDisabled ? "none" : undefined}>
                    {values.length === 0 ? (
                      <Text cursor="default" flexGrow={1}>
                        <FormattedMessage defaultMessage="Sélectionner..." />
                      </Text>
                    ) : (
                      <HStack
                        flexGrow={1}
                        overflow="hidden"
                        sx={
                          isMultiple
                            ? {
                                maskImage:
                                  "linear-gradient(to right, black 0%, black 75%, transparent)",
                              }
                            : undefined
                        }>
                        {values.slice(0, 3).map((itemValue) => {
                          const item = resolveItemByValue(itemValue);
                          return (
                            <Fragment key={itemValue}>
                              {renderSelectedOption({
                                item,
                                itemValue,
                                resolveItemName,
                                removeValue: (params) => {
                                  removeValue({
                                    ...params,
                                    onClose,
                                  });
                                },
                                hydrationDone,
                                isInPopover: false,
                              })}
                            </Fragment>
                          );
                        })}
                      </HStack>
                    )}

                    <IconButton
                      tabIndex={props.isDisabled ? -1 : 0}
                      variant="ghost"
                      colorScheme="darkGray"
                      icon={<Icon as={IoChevronDownOutline} />}
                      aria-label={intl.formatMessage({
                        defaultMessage: "Basculer",
                      })}
                    />
                  </HStack>
                </PopoverTrigger>

                <ConditionalWrapper
                  hasWrapper={false}
                  wrapper={(children) => <Portal>{children}</Portal>}>
                  <PopoverContent width="inherit">
                    <PopoverBody p="0">
                      <SearchProvider
                        preventSubmitIfNotDirty={true}
                        defaultValues={defaultValues}
                        searchId="autocomplete"
                        routerSyncUrl={false}
                        useSessionStorageHydration={false}
                        endpointUrl={endpointUrl}>
                        <Internal
                          value={_value}
                          onClose={onClose}
                          values={values}
                          renderOption={renderOption}
                          renderSelectedOption={renderSelectedOption}
                          toggleValue={(params) => {
                            toggleValue({ ...params, onClose });
                          }}
                          removeValue={(params) => {
                            removeValue({ ...params, onClose });
                          }}
                          reset={reset}
                          resolveItemByValue={resolveItemByValue}
                          resolveItemName={resolveItemName}
                          resolveItemValue={resolveItemValue}
                          inputRef={inputRef}
                          hydrationDone={hydrationDone}
                        />
                      </SearchProvider>
                    </PopoverBody>
                  </PopoverContent>
                </ConditionalWrapper>
              </>
            )}
          </Popover>
        </Box>
      </>
    );
  },
);

export default Autocomplete;
